<script setup>
import { ref, computed, watch } from 'vue'
import { useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import { useToast } from '@/composables/useToast'


const props = defineProps({
  projet:                  { type: Object,  default: () => ({}) },
  roles_utilisateurs:      { type: Array,   default: () => [] },
  activer_fonction:        { type: Boolean, default: false },
  personnels_par_fonction: { type: Array,   default: () => [] },
  personnels:              { type: Array,   default: () => [] },
  show_prenom:             { type: Boolean, default: false },
  show_type_fonction:      { type: Boolean, default: false },
  show_description_perso:  { type: Boolean, default: false },
  type_fonctions:          { type: Array,   default: () => [] },
  activer_type_equipement: { type: Boolean, default: false },
  equipements_par_type:    { type: Array,   default: () => [] },
  equipements:             { type: Array,   default: () => [] },
  show_type_equipement:    { type: Boolean, default: false },
  show_description_equip:  { type: Boolean, default: false },
  type_equipements:        { type: Array,   default: () => [] },
  inventaire_enabled:      { type: Boolean, default: false },
  inventaires:             { type: Array,   default: () => [] },
  show_type_taux:          { type: Boolean, default: false },
  type_taux:               { type: Array,   default: () => [] },
  type_unites:             { type: Array,   default: () => [] },
  type_parties:            { type: Array,   default: () => [] },
  can_see_costs:           { type: Boolean, default: false },
  type_partie_suivies:     { type: Array,   default: () => [] },
  default_partie_suivie_ids: { type: Array, default: () => [] },
  errors:                  { type: Object,  default: () => ({}) },
  projet_url:              { type: String,  default: '/projets' },
  estimations_url:         { type: String,  default: '/estimations' },
})

// ── Helpers lignes dynamiques ────────────────────────────────
function emptyPersonnelExterne() {
  return { code: '', nom: '', prenom: '', type_fonction_id: '', description: '', taux_horaire: '', actif: true, _destroy: false }
}
function emptyEquipementExterne() {
  return { code: '', nom: '', type_equipement_id: '', description: '', taux_horaire: '', actif: true, _destroy: false }
}
function emptyInventaire() {
  return { inventaire_id: '', taux: '', type_taux_id: '', _destroy: false }
}
function emptyTag() {
  return { tag: '', ordre: '', _destroy: false }
}
function emptyElement() {
  return {
    code: '', description: '', quantite: '', type_unite_id: '', estimation_element_tag_id: '',
    _destroy: false,
    estimation_parties_attributes: props.type_parties.map(tp => ({ type_partie_estimation_id: tp.id, prix_coutant: '' })),
  }
}

// ── Formulaire ───────────────────────────────────────────────
const form = useForm({
  estimation: {
    projet_id:         props.projet.id,
    nom:               '',
    description:       '',
    date_soumission:   '',
    date_acceptation:  '',
    utilisateur_ids:   [],
    personnel_ids:     [],
    equipement_ids:    [],
    personnel_externes_attributes:          [],
    equipement_externes_attributes:         [],
    estimation_inventaires_attributes:      [],
    estimation_element_tags_attributes:     [],
    estimation_elements_attributes:         [],
    estimation_partie_suivies_attributes:   props.default_partie_suivie_ids.map(id => ({ type_partie_estimation_id: id })),
  },
})

// ── Lignes dynamiques add/remove ─────────────────────────────
function addPersonnelExterne()   { form.estimation.personnel_externes_attributes.push(emptyPersonnelExterne()) }
function addEquipementExterne()  { form.estimation.equipement_externes_attributes.push(emptyEquipementExterne()) }
function addInventaire()         { form.estimation.estimation_inventaires_attributes.push(emptyInventaire()) }
function addTag()                { form.estimation.estimation_element_tags_attributes.push(emptyTag()) }
function addElement()            { form.estimation.estimation_elements_attributes.push(emptyElement()) }
function addPartieSuivie()       { form.estimation.estimation_partie_suivies_attributes.push({ type_partie_estimation_id: '' }) }

function removeRow(arr, i) { arr.splice(i, 1) }

// ── Tags disponibles pour les items ─────────────────────────
const tagsDisponibles = computed(() =>
  form.estimation.estimation_element_tags_attributes.filter(t => !t._destroy && t.tag)
)

// ── Soumission ───────────────────────────────────────────────
function submit() {
  form.transform(data => ({ estimation: data.estimation })).post('/estimations', { preserveScroll: true })
}

const allErrors = computed(() => ({ ...form.errors, ...props.errors }))
function err(f) { return allErrors.value[f] }

const { error: toastError } = useToast()
watch(allErrors, (errs) => {
  const msgs = Object.entries(errs).map(([f, m]) => `${f} : ${Array.isArray(m) ? m.join(', ') : m}`)
  if (msgs.length) toastError(msgs.join('\n'))
}, { deep: true })

// ── Classes utilitaires ──────────────────────────────────────
const inputCls = 'w-full bg-surface-container-highest border border-outline-variant/20 rounded-md px-3 py-2 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all'
const inputSmCls = 'w-full bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1.5 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30'
const sectionHeaderCls = 'px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40 flex items-center justify-between'
const sectionTitleCls = 'text-[11px] font-black uppercase tracking-widest text-outline'
const labelCls = 'block text-[10px] font-black uppercase tracking-widest text-outline mb-1.5'
const addBtnCls = 'flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-primary hover:brightness-125 transition-all'
const removeBtnCls = 'text-outline hover:text-error transition-colors'

</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="flex items-center gap-3">
        <a :href="projet_url" class="text-outline hover:text-on-surface transition-colors">
          <span class="material-symbols-outlined text-xl">arrow_back</span>
        </a>
        <span class="text-outline">/</span>
        <span class="text-sm font-black uppercase tracking-widest text-on-surface">Nouveau bordereau</span>
        <span class="text-outline">/</span>
        <span class="font-mono text-sm font-bold text-primary">{{ projet.code }}</span>
      </div>
    </template>

    <div class="p-8">
      <div class="space-y-6">

        <!-- En-tête -->
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-black text-on-surface tracking-tight uppercase">Nouveau bordereau</h1>
            <p class="text-sm text-outline mt-1">
              Projet <span class="font-mono font-bold text-primary">{{ projet.code }}</span> — {{ projet.nom }}
            </p>
          </div>
        </div>

        <!-- ── 1. Détails ── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div :class="sectionHeaderCls">
            <h2 :class="sectionTitleCls">Détails</h2>
          </div>
          <div class="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label :class="labelCls">Projet</label>
              <div class="px-3 py-2 rounded-md bg-surface-container-highest text-sm font-mono font-bold text-primary">
                {{ projet.code }} — {{ projet.nom }}
              </div>
            </div>
            <div>
              <label :class="labelCls">Nom <span class="text-error">*</span></label>
              <input v-model="form.estimation.nom" type="text" :class="[inputCls, err('nom') ? 'ring-2 ring-error/40' : '']" />
              <p v-if="err('nom')" class="text-xs text-error mt-1">{{ err('nom') }}</p>
            </div>
            <div>
              <label :class="labelCls">Date de soumission</label>
              <input v-model="form.estimation.date_soumission" type="date" :class="inputCls" />
            </div>
            <div>
              <label :class="labelCls">Date d'acceptation</label>
              <input v-model="form.estimation.date_acceptation" type="date" :class="inputCls" />
            </div>
            <div class="sm:col-span-2">
              <label :class="labelCls">Description</label>
              <textarea v-model="form.estimation.description" rows="3" :class="inputCls + ' resize-none'"></textarea>
            </div>
          </div>
        </div>

        <!-- ── 2. Utilisateurs ── -->
        <div v-if="roles_utilisateurs.length > 0" class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div :class="sectionHeaderCls">
            <h2 :class="sectionTitleCls">Utilisateurs assignés</h2>
          </div>
          <div class="p-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            <div v-for="role in roles_utilisateurs" :key="role.nom">
              <p class="text-[10px] font-black uppercase tracking-widest text-outline mb-2">{{ role.nom }}</p>
              <div class="space-y-1.5">
                <label v-for="u in role.utilisateurs" :key="u.id" class="flex items-center gap-2 cursor-pointer group">
                  <input type="checkbox" :value="u.id" v-model="form.estimation.utilisateur_ids"
                         class="rounded border-outline-variant/40 text-primary focus:ring-primary/20" />
                  <span class="text-sm text-on-surface group-hover:text-primary transition-colors">{{ u.nom_complet }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- ── 3. Personnel interne ── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div :class="sectionHeaderCls">
            <h2 :class="sectionTitleCls">Personnel</h2>
          </div>
          <div class="p-6">
            <!-- Groupé par type_fonction -->
            <div v-if="activer_fonction" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              <div v-for="groupe in personnels_par_fonction" :key="groupe.code">
                <p class="text-[10px] font-black uppercase tracking-widest text-outline mb-2">{{ groupe.code }}</p>
                <div class="space-y-1.5">
                  <label v-for="p in groupe.personnels" :key="p.id" class="flex items-center gap-2 cursor-pointer group">
                    <input type="checkbox" :value="p.id" v-model="form.estimation.personnel_ids"
                           class="rounded border-outline-variant/40 text-primary focus:ring-primary/20" />
                    <span class="text-sm text-on-surface group-hover:text-primary transition-colors">{{ p.nom_complet }}</span>
                  </label>
                </div>
              </div>
            </div>
            <!-- Liste plate -->
            <div v-else class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              <label v-for="p in personnels" :key="p.id" class="flex items-center gap-2 cursor-pointer group">
                <input type="checkbox" :value="p.id" v-model="form.estimation.personnel_ids"
                       class="rounded border-outline-variant/40 text-primary focus:ring-primary/20" />
                <span class="text-sm text-on-surface group-hover:text-primary transition-colors">{{ p.nom_complet }}</span>
              </label>
            </div>
          </div>
        </div>

        <!-- ── 4. Personnel externe ── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div :class="sectionHeaderCls">
            <h2 :class="sectionTitleCls">Personnel externe <span class="text-outline/50">({{ form.estimation.personnel_externes_attributes.length }})</span></h2>
            <button type="button" @click="addPersonnelExterne" :class="addBtnCls">
              <span class="material-symbols-outlined text-sm">add</span>Ajouter
            </button>
          </div>
          <div v-if="form.estimation.personnel_externes_attributes.length === 0" class="px-6 py-8 text-center text-outline text-sm">
            <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">person_add</span>
            Aucun personnel externe
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-24">Code</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Nom</th>
                  <th v-if="show_prenom" class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Prénom</th>
                  <th v-if="show_type_fonction" class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Profession</th>
                  <th v-if="show_description_perso" class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Description</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-24">Taux/h</th>
                  <th class="px-4 py-3 text-center text-[10px] font-black uppercase tracking-widest text-outline w-16">Actif</th>
                  <th class="px-4 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="(pe, i) in form.estimation.personnel_externes_attributes" :key="i" class="hover:bg-surface-container-highest/10">
                  <td class="px-4 py-2"><input v-model="pe.code" type="text" :class="inputSmCls" /></td>
                  <td class="px-4 py-2"><input v-model="pe.nom" type="text" :class="inputSmCls" /></td>
                  <td v-if="show_prenom" class="px-4 py-2"><input v-model="pe.prenom" type="text" :class="inputSmCls" /></td>
                  <td v-if="show_type_fonction" class="px-4 py-2">
                    <select v-model="pe.type_fonction_id" :class="inputSmCls">
                      <option value="">—</option>
                      <option v-for="f in type_fonctions" :key="f.id" :value="f.id">{{ f.label }}</option>
                    </select>
                  </td>
                  <td v-if="show_description_perso" class="px-4 py-2"><input v-model="pe.description" type="text" :class="inputSmCls" /></td>
                  <td class="px-4 py-2"><input v-model="pe.taux_horaire" type="number" step="0.01" :class="inputSmCls" /></td>
                  <td class="px-4 py-2 text-center">
                    <input type="checkbox" v-model="pe.actif" class="rounded border-outline-variant/40 text-primary focus:ring-primary/20" />
                  </td>
                  <td class="px-4 py-2 text-center">
                    <button type="button" @click="removeRow(form.estimation.personnel_externes_attributes, i)" :class="removeBtnCls">
                      <span class="material-symbols-outlined text-base">delete</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── 5. Équipements internes ── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div :class="sectionHeaderCls">
            <h2 :class="sectionTitleCls">Équipements</h2>
          </div>
          <div class="p-6">
            <!-- Groupé par type -->
            <div v-if="activer_type_equipement" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              <div v-for="groupe in equipements_par_type" :key="groupe.code">
                <p class="text-[10px] font-black uppercase tracking-widest text-outline mb-2">{{ groupe.code }}</p>
                <div class="space-y-1.5">
                  <label v-for="eq in groupe.equipements" :key="eq.id" class="flex items-center gap-2 cursor-pointer group">
                    <input type="checkbox" :value="eq.id" v-model="form.estimation.equipement_ids"
                           class="rounded border-outline-variant/40 text-primary focus:ring-primary/20" />
                    <span class="text-sm text-on-surface group-hover:text-primary transition-colors">{{ eq.nom }}</span>
                  </label>
                </div>
              </div>
            </div>
            <!-- Liste plate -->
            <div v-else class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              <label v-for="eq in equipements" :key="eq.id" class="flex items-center gap-2 cursor-pointer group">
                <input type="checkbox" :value="eq.id" v-model="form.estimation.equipement_ids"
                       class="rounded border-outline-variant/40 text-primary focus:ring-primary/20" />
                <span class="text-sm text-on-surface group-hover:text-primary transition-colors">{{ eq.nom }}</span>
              </label>
            </div>
          </div>
        </div>

        <!-- ── 6. Équipement externe ── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div :class="sectionHeaderCls">
            <h2 :class="sectionTitleCls">Équipements externes <span class="text-outline/50">({{ form.estimation.equipement_externes_attributes.length }})</span></h2>
            <button type="button" @click="addEquipementExterne" :class="addBtnCls">
              <span class="material-symbols-outlined text-sm">add</span>Ajouter
            </button>
          </div>
          <div v-if="form.estimation.equipement_externes_attributes.length === 0" class="px-6 py-8 text-center text-outline text-sm">
            <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">construction</span>
            Aucun équipement externe
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-24">Code</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Nom</th>
                  <th v-if="show_type_equipement" class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Type</th>
                  <th v-if="show_description_equip" class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Description</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-24">Taux/h</th>
                  <th class="px-4 py-3 text-center text-[10px] font-black uppercase tracking-widest text-outline w-16">Actif</th>
                  <th class="px-4 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="(ee, i) in form.estimation.equipement_externes_attributes" :key="i" class="hover:bg-surface-container-highest/10">
                  <td class="px-4 py-2"><input v-model="ee.code" type="text" :class="inputSmCls" /></td>
                  <td class="px-4 py-2"><input v-model="ee.nom" type="text" :class="inputSmCls" /></td>
                  <td v-if="show_type_equipement" class="px-4 py-2">
                    <select v-model="ee.type_equipement_id" :class="inputSmCls">
                      <option value="">—</option>
                      <option v-for="t in type_equipements" :key="t.id" :value="t.id">{{ t.label }}</option>
                    </select>
                  </td>
                  <td v-if="show_description_equip" class="px-4 py-2"><input v-model="ee.description" type="text" :class="inputSmCls" /></td>
                  <td class="px-4 py-2"><input v-model="ee.taux_horaire" type="number" step="0.01" :class="inputSmCls" /></td>
                  <td class="px-4 py-2 text-center">
                    <input type="checkbox" v-model="ee.actif" class="rounded border-outline-variant/40 text-primary focus:ring-primary/20" />
                  </td>
                  <td class="px-4 py-2 text-center">
                    <button type="button" @click="removeRow(form.estimation.equipement_externes_attributes, i)" :class="removeBtnCls">
                      <span class="material-symbols-outlined text-base">delete</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── 7. Inventaires (conditionnel) ── -->
        <div v-if="inventaire_enabled" class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div :class="sectionHeaderCls">
            <h2 :class="sectionTitleCls">Inventaire <span class="text-outline/50">({{ form.estimation.estimation_inventaires_attributes.length }})</span></h2>
            <button type="button" @click="addInventaire" :class="addBtnCls">
              <span class="material-symbols-outlined text-sm">add</span>Ajouter
            </button>
          </div>
          <div v-if="form.estimation.estimation_inventaires_attributes.length === 0" class="px-6 py-8 text-center text-outline text-sm">
            <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">inventory_2</span>
            Aucun inventaire
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Inventaire</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-28">Taux</th>
                  <th v-if="show_type_taux" class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-32">Type taux</th>
                  <th class="px-4 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="(inv, i) in form.estimation.estimation_inventaires_attributes" :key="i" class="hover:bg-surface-container-highest/10">
                  <td class="px-4 py-2">
                    <select v-model="inv.inventaire_id" :class="inputSmCls">
                      <option value="">— Sélectionner —</option>
                      <option v-for="inv2 in inventaires" :key="inv2.id" :value="inv2.id">{{ inv2.label }}</option>
                    </select>
                  </td>
                  <td class="px-4 py-2"><input v-model="inv.taux" type="number" step="0.01" :class="inputSmCls" /></td>
                  <td v-if="show_type_taux" class="px-4 py-2">
                    <select v-model="inv.type_taux_id" :class="inputSmCls">
                      <option v-for="tt in type_taux" :key="tt.id" :value="tt.id">{{ tt.code }}</option>
                    </select>
                  </td>
                  <td class="px-4 py-2 text-center">
                    <button type="button" @click="removeRow(form.estimation.estimation_inventaires_attributes, i)" :class="removeBtnCls">
                      <span class="material-symbols-outlined text-base">delete</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── 8. Catégories d'items ── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div :class="sectionHeaderCls">
            <h2 :class="sectionTitleCls">Catégories d'items <span class="text-outline/50">({{ form.estimation.estimation_element_tags_attributes.length }})</span></h2>
            <button type="button" @click="addTag" :class="addBtnCls">
              <span class="material-symbols-outlined text-sm">add</span>Ajouter
            </button>
          </div>
          <div v-if="form.estimation.estimation_element_tags_attributes.length === 0" class="px-6 py-8 text-center text-outline text-sm">
            <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">label</span>
            Aucune catégorie
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Nom de la catégorie</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-24">Ordre</th>
                  <th class="px-4 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="(tag, i) in form.estimation.estimation_element_tags_attributes" :key="i" class="hover:bg-surface-container-highest/10">
                  <td class="px-4 py-2"><input v-model="tag.tag" type="text" :class="inputSmCls" /></td>
                  <td class="px-4 py-2"><input v-model="tag.ordre" type="number" :class="inputSmCls" /></td>
                  <td class="px-4 py-2 text-center">
                    <button type="button" @click="removeRow(form.estimation.estimation_element_tags_attributes, i)" :class="removeBtnCls">
                      <span class="material-symbols-outlined text-base">delete</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── 9. Items du bordereau ── -->
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div :class="sectionHeaderCls">
            <h2 :class="sectionTitleCls">Items <span class="text-outline/50">({{ form.estimation.estimation_elements_attributes.length }})</span></h2>
            <button type="button" @click="addElement" :class="addBtnCls">
              <span class="material-symbols-outlined text-sm">add</span>Ajouter
            </button>
          </div>
          <div v-if="form.estimation.estimation_elements_attributes.length === 0" class="px-6 py-8 text-center text-outline text-sm">
            <span class="material-symbols-outlined text-3xl block mb-2 opacity-30">list_alt</span>
            Aucun item — cliquez sur Ajouter
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-20">Code</th>
                  <th v-if="tagsDisponibles.length > 0" class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-32">Catégorie</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline">Description</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-20">Qté</th>
                  <th class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-20">Unité</th>
                  <template v-if="can_see_costs">
                    <th v-for="tp in type_parties" :key="tp.id"
                        class="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline w-24">
                      {{ tp.code }}
                    </th>
                  </template>
                  <th class="px-4 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <template v-for="(el, i) in form.estimation.estimation_elements_attributes" :key="i">
                  <tr v-if="!el._destroy" class="hover:bg-surface-container-highest/10">
                    <td class="px-4 py-2"><input v-model="el.code" type="text" :class="inputSmCls" /></td>
                    <td v-if="tagsDisponibles.length > 0" class="px-4 py-2">
                      <select v-model="el.estimation_element_tag_id" :class="inputSmCls">
                        <option value="">—</option>
                        <option v-for="tag in tagsDisponibles" :key="tag.tag" :value="tag.tag">{{ tag.tag }}</option>
                      </select>
                    </td>
                    <td class="px-4 py-2">
                      <input v-model="el.description" type="text" :class="inputSmCls" />
                    </td>
                    <td class="px-4 py-2"><input v-model="el.quantite" type="number" step="0.01" :class="inputSmCls" /></td>
                    <td class="px-4 py-2">
                      <select v-model="el.type_unite_id" :class="inputSmCls">
                        <option value="">—</option>
                        <option v-for="u in type_unites" :key="u.id" :value="u.id">{{ u.code }}</option>
                      </select>
                    </td>
                    <template v-if="can_see_costs">
                      <td v-for="(ep, j) in el.estimation_parties_attributes" :key="j" class="px-4 py-2">
                        <input v-model="ep.prix_coutant" type="number" step="0.01" :class="inputSmCls" />
                      </td>
                    </template>
                    <td class="px-4 py-2 text-center">
                      <button type="button" @click="removeRow(form.estimation.estimation_elements_attributes, i)" :class="removeBtnCls">
                        <span class="material-symbols-outlined text-base">delete</span>
                      </button>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── 10. Parties suivies ── -->
        <div v-if="type_partie_suivies.length > 0" class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">
          <div :class="sectionHeaderCls">
            <h2 :class="sectionTitleCls">Parties suivies <span class="text-outline/50">({{ form.estimation.estimation_partie_suivies_attributes.length }})</span></h2>
            <button type="button" @click="addPartieSuivie" :class="addBtnCls">
              <span class="material-symbols-outlined text-sm">add</span>Ajouter
            </button>
          </div>
          <div class="p-6 space-y-3">
            <div v-for="(ps, i) in form.estimation.estimation_partie_suivies_attributes" :key="i"
                 class="flex items-center gap-3">
              <select v-model="ps.type_partie_estimation_id" :class="inputCls + ' flex-1'">
                <option value="">— Sélectionner —</option>
                <option v-for="tp in type_partie_suivies" :key="tp.id" :value="tp.id">{{ tp.code }}</option>
              </select>
              <button type="button" @click="removeRow(form.estimation.estimation_partie_suivies_attributes, i)" :class="removeBtnCls">
                <span class="material-symbols-outlined text-base">delete</span>
              </button>
            </div>
            <div v-if="form.estimation.estimation_partie_suivies_attributes.length === 0"
                 class="text-sm text-outline text-center py-4">Aucune partie suivie sélectionnée</div>
          </div>
        </div>

        <!-- ── Actions ── -->
        <div class="flex items-center justify-between pt-2 pb-8">
          <a :href="projet_url"
             class="px-5 py-2.5 rounded-md text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container border border-outline-variant/20 hover:bg-surface-container-highest transition-all">
            Annuler
          </a>
          <button type="button" :disabled="form.processing" @click="submit"
                  class="machined-sheen px-8 py-2.5 rounded-md text-on-primary font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:brightness-110 active:scale-95 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
            <span v-if="form.processing" class="material-symbols-outlined text-sm animate-spin">progress_activity</span>
            <span v-else class="material-symbols-outlined text-sm">save</span>
            Créer le bordereau
          </button>
        </div>

      </div>
    </div>
  </AppLayout>
</template>
